import {
  acceptInviteRequestSchema,
  blockOrgRequestSchema,
  blockOrgResponseSchema,
  createInviteRequestSchema,
  createSlotRequestSchema,
  interviewCalendarResponseSchema,
  noShowResponseSchema,
  interviewSlotSchema,
  inviteDetailResponseSchema,
  inviteListQuerySchema,
  inviteListResponseSchema,
  requestCancellationRequestSchema,
  slotListResponseSchema,
  type AcceptInviteRequest,
  type CreateInviteRequest,
  type CreateSlotRequest,
  type InterviewCalendarQuery,
  type RequestCancellationRequest,
} from "@chotca/config";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export function createInvitesResource(ctx: RequestContext) {
  return {
    // ---------- employer calendar ----------
    interviews(query: InterviewCalendarQuery = {}) {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/interviews",
        query,
        dataSchema: interviewCalendarResponseSchema,
        auth: true,
      });
    },

    /** Employer reports a no-show on a committed interview. */
    reportNoShow(inviteId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/invites/${inviteId}/no-show`,
        dataSchema: noShowResponseSchema,
        auth: true,
      });
    },

    // ---------- worker side ----------
    mine(query: { cursor?: string; limit?: number } = {}) {
      const q = inviteListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: "/me/invites",
        query: q,
        dataSchema: inviteListResponseSchema,
        auth: true,
      });
    },
    detail(inviteId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/me/invites/${inviteId}`,
        dataSchema: inviteDetailResponseSchema,
        auth: true,
      });
    },
    accept(inviteId: string, body: AcceptInviteRequest) {
      const payload = acceptInviteRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/me/invites/${inviteId}/accept`,
        body: payload,
        dataSchema: inviteDetailResponseSchema,
        auth: true,
      });
    },
    reject(inviteId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/me/invites/${inviteId}/reject`,
        dataSchema: inviteDetailResponseSchema,
        auth: true,
      });
    },
    reschedule(inviteId: string, body: AcceptInviteRequest) {
      const payload = acceptInviteRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/me/invites/${inviteId}/reschedule`,
        body: payload,
        dataSchema: inviteDetailResponseSchema,
        auth: true,
      });
    },
    cancelInWindow(inviteId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/me/invites/${inviteId}/cancel`,
        dataSchema: inviteDetailResponseSchema,
        auth: true,
      });
    },
    requestCancellation(inviteId: string, body: RequestCancellationRequest) {
      const payload = requestCancellationRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/invites/${inviteId}/request-cancellation`,
        body: payload,
        dataSchema: requestCancellationRequestSchema,
        auth: true,
      });
    },

    // ---------- employer side ----------
    byOrg(orgId: string, query: { cursor?: string; limit?: number } = {}) {
      const q = inviteListQuerySchema.parse(query);
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/invites`,
        query: q,
        dataSchema: inviteListResponseSchema,
        auth: true,
      });
    },
    create(orgId: string, body: CreateInviteRequest) {
      const payload = createInviteRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/invites`,
        body: payload,
        dataSchema: inviteDetailResponseSchema,
        auth: true,
      });
    },

    // ---------- worker blocks org ("Ẩn quán này") ----------
    blockOrg(orgId: string) {
      const payload = blockOrgRequestSchema.parse({ orgId });
      return requestJson(ctx, {
        method: "POST",
        path: "/me/blocked-orgs",
        body: payload,
        dataSchema: blockOrgResponseSchema,
        auth: true,
      });
    },
    unblockOrg(orgId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/me/blocked-orgs/${orgId}`,
        dataSchema: blockOrgResponseSchema,
        auth: true,
      });
    },

    // ---------- slots ----------
    listSlotsByLocation(orgId: string, locationId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/locations/${locationId}/slots`,
        dataSchema: slotListResponseSchema,
        auth: true,
      });
    },
    createSlot(orgId: string, body: CreateSlotRequest) {
      const payload = createSlotRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/slots`,
        body: payload,
        dataSchema: interviewSlotSchema,
        auth: true,
      });
    },
  };
}

export type InvitesResource = ReturnType<typeof createInvitesResource>;
